import { useTranslation } from "react-i18next"
import { format } from "date-fns"
import { LoaderCircle, RefreshCwIcon } from "lucide-react"

import EmptyContainer from "@/components/EmptyContainer"
import Icon from "@/components/Icon"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/helpers/classNames"
import { isString } from "@/helpers/typeguards"
import { formatArrayValues, formatStringValue } from "@/helpers/valueFormat"
import { useRefreshCaseChannels } from "@/hooks/useRefreshCaseChannels"
import { ActivityFeedEntry, Decoder } from "@/pages/CustomConnectPanel/value"

import CollapsibleQuestion from "./CollapsibleQuestion"
import HistoryItemCard from "./HistoryItemCard"
import {
  type ChannelDetailInformation,
  getSelectedChannelDetails,
} from "./hook"

type Props = {
  historyEntries: Decoder.CaseAndActivityFeeds["activity_feed"]
  studentCase?: Decoder.CaseAndActivityFeeds["case"]
}

export function CallViewHistoryTabs({ historyEntries, studentCase }: Props) {
  const { t } = useTranslation()

  return (
    <Tabs defaultValue="history" className="w-full">
      <TabsList className="bg-neutral-100">
        <TabsTrigger value="history">
          <HistoryTabTitle count={historyEntries.length} />
        </TabsTrigger>
        <TabsTrigger value="detailedInformation">
          {t("caseHistory.tabs.detailedInformation")}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="history" className="mt-0">
        <HistoryEntries historyEntries={historyEntries} />
      </TabsContent>
      <TabsContent value="detailedInformation" className="mt-0 bg-neutral-100">
        <StudentDetailsTabContent studentCase={studentCase} />
      </TabsContent>
    </Tabs>
  )
}

const HistoryTabTitle = ({ count }: { count: number }) => {
  const { t } = useTranslation()

  return (
    <div className={cn("flex items-center gap-x-1")}>
      <div data-unmask>{t("caseHistory.tabs.history")}</div>
      <Icon name="dot" size="xxsm" className={"text-neutral-500"} />
      <div data-unmask>{t("caseHistory.tabs.numberOfItems", { count })}</div>
    </div>
  )
}

function HistoryEntries({
  historyEntries,
}: {
  historyEntries: ActivityFeedEntry.ActivityFeedEntryType[]
}) {
  return (
    <div className={cn("w-full bg-neutral-200 pt-1")}>
      <div
        className={cn(
          "flex h-[450px] flex-col gap-y-4 overflow-y-scroll px-6 py-4",
          `
            w-full ring-offset-background
            focus-visible:ring-2 focus-visible:ring-ring
            focus-visible:ring-offset-2 focus-visible:outline-hidden
          `,
        )}
        tabIndex={0}
      >
        {historyEntries.map((event, i) => (
          <HistoryItemCard event={event} key={i} />
        ))}
      </div>
    </div>
  )
}

function ChannelHeader({
  channelTitle,
  registrationDate,
}: {
  channelTitle: string
  registrationDate: Date | null
}) {
  const { t } = useTranslation()

  return (
    <div className={cn("flex gap-x-4")}>
      <div className={cn("flex w-1/3 flex-col gap-y-2")}>
        <div className={cn("w-full font-label-small text-neutral-600")}>
          {t("caseHistory.applicationDate")}
        </div>
        <p className={cn("w-full font-body-medium-regular text-neutral-700")}>
          {registrationDate ? format(registrationDate, "yyyy/MM/dd") : "-"}
        </p>
      </div>
      <div className={cn("flex w-2/3 flex-col gap-y-2")}>
        <div className={cn("w-full font-label-small text-neutral-600")}>
          {t("caseHistory.applicationRoute")}
        </div>
        <p className={cn("w-full font-body-medium-regular text-neutral-700")}>
          {channelTitle}
        </p>
      </div>
    </div>
  )
}

function ChannelDetails({
  channelTitle,
  preferredCompanies,
  preferredIndustries,
  preferredJobTypes,
  registrationDate,
  selectedTextualQuestion,
}: ChannelDetailInformation) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-y-4",
        `
          ring-offset-background
          focus-visible:ring-2 focus-visible:ring-ring
          focus-visible:ring-offset-2 focus-visible:outline-hidden
        `,
      )}
      tabIndex={0}
    >
      <ChannelHeader
        channelTitle={channelTitle}
        registrationDate={registrationDate}
      />
      {isString(selectedTextualQuestion?.question) && (
        <CollapsibleQuestion
          question={selectedTextualQuestion.question}
          answer={formatStringValue(selectedTextualQuestion.answer)}
        />
      )}

      <div className="flex justify-between">
        {isString(preferredIndustries?.question) && (
          <div className="grow">
            <CollapsibleQuestion
              question={preferredIndustries.question}
              answer={
                preferredIndustries?.answers?.length
                  ? formatArrayValues(preferredIndustries.answers)
                  : "—"
              }
            />
          </div>
        )}

        {isString(preferredJobTypes?.question) && (
          <div className="grow">
            <CollapsibleQuestion
              question={preferredJobTypes.question}
              answer={
                preferredJobTypes?.answers?.length
                  ? formatArrayValues(preferredJobTypes.answers)
                  : "—"
              }
            />
          </div>
        )}
      </div>

      {isString(preferredCompanies?.question) && (
        <CollapsibleQuestion
          question={preferredCompanies.question}
          answer={
            preferredCompanies?.answers?.length
              ? formatArrayValues(preferredCompanies.answers)
              : "—"
          }
        />
      )}
    </div>
  )
}

/**
 * Content of the second tab of the history section: show only the last questions
 */
export function StudentDetailsTabContent({
  studentCase,
}: {
  studentCase?: Decoder.Case
}) {
  const { t } = useTranslation()
  const refreshMutation = useRefreshCaseChannels(studentCase?._id)
  const channelDetailInformationList = studentCase
    ? getSelectedChannelDetails(studentCase)
    : []

  return (
    <div
      className={cn(
        "relative h-[450px] w-full bg-neutral-100 pt-1",
        "flex flex-col gap-y-4 overflow-y-scroll px-6 py-4",
      )}
    >
      {studentCase && (
        <button
          type="button"
          className="
            absolute top-4 right-6 z-10 flex cursor-pointer items-center gap-1
            text-xs text-neutral-600
            disabled:cursor-default disabled:opacity-50
          "
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isDisabled}
        >
          {refreshMutation.isPending ? (
            <LoaderCircle className="size-3 animate-spin" />
          ) : (
            <RefreshCwIcon className="size-3" />
          )}
          {t("caseHistory.syncQA")}
        </button>
      )}
      {channelDetailInformationList.length ? (
        channelDetailInformationList.map((channelInformation, idx) => (
          <ChannelDetails key={idx} {...channelInformation} />
        ))
      ) : (
        <EmptyContainer />
      )}
    </div>
  )
}
